import { useEffect, useState } from "react";
import { Button } from "@/components/common/Button";
import { Spinner } from "@/components/common/Spinner";
import { useOnboardingStore } from "@/stores/onboardingStore";
import { LiveLogStream } from "../primitives/LiveLogStream";
import { UACWarning } from "../primitives/UACWarning";

/**
 * Third step — runs the install queue produced by the env check.
 *
 * If the queue contains Git for Windows, we show a UAC heads-up
 * *before* the user presses Install. After each install succeeds the
 * store automatically pulls the next one; this component is purely
 * about visualising progress and handling retries.
 */
export function InstallStep() {
  const {
    environment,
    installQueue,
    activeInstall,
    startNextInstall,
    pollActiveInstall,
    goNext,
  } = useOnboardingStore();

  const [uacAcknowledged, setUacAcknowledged] = useState(false);

  // Poll at ~400ms while an install is running.
  useEffect(() => {
    if (!activeInstall) return;
    if (activeInstall.status !== "running" && activeInstall.status !== "pending") return;
    const id = window.setInterval(() => {
      pollActiveInstall().catch(() => {});
    }, 400);
    return () => window.clearInterval(id);
  }, [activeInstall?.jobId, activeInstall?.status, pollActiveInstall]);

  // If the queue drained and everything succeeded, auto-advance.
  useEffect(() => {
    if (
      installQueue.length === 0 &&
      activeInstall?.status === "succeeded"
    ) {
      const id = window.setTimeout(() => goNext(), 750);
      return () => window.clearTimeout(id);
    }
  }, [installQueue.length, activeInstall?.status, goNext]);

  const currentKind = installQueue[0] ?? activeInstall?.kind ?? "claude_cli";
  const needsUacWarning =
    environment?.platform.os === "windows" &&
    currentKind === "git_for_windows" &&
    (!activeInstall || activeInstall.status === "pending");

  const toolTitle =
    currentKind === "git_for_windows" ? "Git for Windows" : "Claude Code";

  const running = activeInstall?.status === "running" || activeInstall?.status === "pending";
  const failed = activeInstall?.status === "failed" || activeInstall?.status === "cancelled";
  const succeeded = activeInstall?.status === "succeeded";

  const handleStart = async () => {
    await startNextInstall();
  };

  const handleRetry = async () => {
    await startNextInstall();
  };

  return (
    <div className="w-full">
      <h1 className="text-[32px] font-bold leading-[1.1] tracking-[-0.01em] text-space">
        {succeeded && installQueue.length === 0
          ? "All set. Moving you along…"
          : running
            ? `Installing ${toolTitle}…`
            : failed
              ? `Couldn't install ${toolTitle}`
              : `I'll install ${toolTitle} now`}
      </h1>
      <p className="mt-1.5 text-[14px] leading-relaxed text-space-50">
        {succeeded && installQueue.length === 0
          ? "Setting up your workspace access is next."
          : running
            ? "This runs in the background. You don't need to do anything."
            : failed
              ? "Something went wrong. The log below has the details."
              : currentKind === "git_for_windows"
                ? "Quick one — Claude Code uses git on Windows to run its tools."
                : "The installer is Anthropic's official native binary. No admin access needed."}
      </p>

      {/* UAC heads-up (Windows + Git only, before the run starts) */}
      {needsUacWarning && !uacAcknowledged && (
        <div className="mt-6">
          <UACWarning tool="Git for Windows" />
          <div className="mt-4 flex justify-end">
            <Button
              variant="primary"
              size="md"
              onClick={() => setUacAcknowledged(true)}
            >
              Got it
            </Button>
          </div>
        </div>
      )}

      {/* Start button — shown when no job is running yet */}
      {(!activeInstall || (failed && !running)) &&
        (!needsUacWarning || uacAcknowledged) && (
          <div className="mt-6">
            <Button
              variant="primary"
              size="lg"
              className="w-full"
              onClick={failed ? handleRetry : handleStart}
            >
              {failed ? "Try again" : `Install ${toolTitle}`}
            </Button>
          </div>
        )}

      {/* Log stream while running or after completion */}
      {activeInstall && (
        <div className="mt-6">
          <div className="mb-2 flex items-center gap-2 text-[12px] font-medium tracking-[0.04em] text-space-50">
            {running && <Spinner size="sm" className="text-jove" />}
            <span>
              {running
                ? `Installing… ${activeInstall.durationSeconds.toFixed(1)}s elapsed`
                : succeeded
                  ? `Installed in ${activeInstall.durationSeconds.toFixed(1)}s`
                  : failed
                    ? "Install did not complete"
                    : ""}
            </span>
          </div>
          <LiveLogStream
            lines={activeInstall.log}
            running={running}
            maxHeight={220}
          />
        </div>
      )}

      {/* Error recovery */}
      {failed && activeInstall?.error && (
        <div className="mt-4 rounded-xl border border-hydra/30 bg-hydra-50 px-4 py-3">
          <p className="text-[14px] text-hydra">{activeInstall.error}</p>
          <div className="mt-3 flex gap-2">
            <Button variant="primary" size="sm" onClick={handleRetry}>
              Try again
            </Button>
            <a
              href={
                currentKind === "git_for_windows"
                  ? "https://git-scm.com/downloads/win"
                  : "https://claude.ai/install.sh"
              }
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center rounded-lg px-3 py-1.5 text-sm font-medium text-space-50 hover:text-space"
            >
              Install manually →
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
